// Help Support Drawer — Refined v4 (search-functional)

import { AlertCircle, ArrowRight, BookOpen, CheckCircle2, HelpCircle, Info, Lightbulb, Search, X } from 'lucide-react';
import * as React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { HelpContent, HelpStepItem } from '@/types/help-support';

interface HelpSupportDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: HelpContent;
}

// ─── Highlight helper ─────────────────────────────────────────────────────────
// Wraps matched substrings in a <mark> span so the user can see why a result matched.
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 dark:bg-yellow-700/60 text-inherit rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export function HelpSupportDrawer({ open, onOpenChange, content }: HelpSupportDrawerProps) {
  const [search, setSearch] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('functionality');
  // Track manually-opened accordion items when NOT searching
  const [openSteps, setOpenSteps] = React.useState<string[]>([]);
  const [openFaqs, setOpenFaqs] = React.useState<string[]>([]);

  // Clear state when drawer closes
  React.useEffect(() => {
    if (!open) {
      setSearch('');
      setActiveTab('functionality');
      setOpenSteps([]);
      setOpenFaqs([]);
    }
  }, [open]);

  const lowerSearch = search.toLowerCase().trim();

  // ── Filtered data (each item carries its original index for stable keys) ───

  const filteredFaq = React.useMemo(() => {
    if (!lowerSearch) return content.faq.map((item, i) => ({ item, originalIndex: i }));
    return content.faq
      .map((item, i) => ({ item, originalIndex: i }))
      .filter(
        ({ item }) =>
          item.question.toLowerCase().includes(lowerSearch) ||
          item.answer.toLowerCase().includes(lowerSearch) ||
          (item.steps ?? []).some((s) => s.toLowerCase().includes(lowerSearch)),
      );
  }, [content.faq, lowerSearch]);

  const filteredSteps = React.useMemo(() => {
    if (!lowerSearch) return content.userGuide.steps.map((step, i) => ({ step, originalIndex: i }));
    return content.userGuide.steps
      .map((step, i) => ({ step, originalIndex: i }))
      .filter(
        ({ step }) =>
          step.title.toLowerCase().includes(lowerSearch) ||
          step.items.some(
            (item) =>
              item.text.toLowerCase().includes(lowerSearch) ||
              (item.note ?? '').toLowerCase().includes(lowerSearch) ||
              (item.tip ?? '').toLowerCase().includes(lowerSearch) ||
              (item.subSteps ?? []).some((s) => s.toLowerCase().includes(lowerSearch)),
          ),
      );
  }, [content.userGuide.steps, lowerSearch]);

  const filteredFeatures = React.useMemo(() => {
    if (!lowerSearch) return content.functionality.keyFeatures;
    return content.functionality.keyFeatures.filter(
      (f) => f.label.toLowerCase().includes(lowerSearch) || f.detail.toLowerCase().includes(lowerSearch),
    );
  }, [content.functionality.keyFeatures, lowerSearch]);

  const filteredSections = React.useMemo(() => {
    if (!lowerSearch) return content.functionality.sectionsAndFields;
    return content.functionality.sectionsAndFields.filter(
      (s) => s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch),
    );
  }, [content.functionality.sectionsAndFields, lowerSearch]);

  const filteredUseCases = React.useMemo(() => {
    if (!lowerSearch) return content.functionality.useCases;
    return content.functionality.useCases.filter((u) => u.toLowerCase().includes(lowerSearch));
  }, [content.functionality.useCases, lowerSearch]);

  const filteredProTips = React.useMemo(() => {
    if (!lowerSearch) return content.userGuide.proTips;
    return content.userGuide.proTips.filter((t) => t.toLowerCase().includes(lowerSearch));
  }, [content.userGuide.proTips, lowerSearch]);

  const showOverview = !lowerSearch || content.functionality.overview.toLowerCase().includes(lowerSearch);
  const showGettingStarted = !lowerSearch || content.userGuide.gettingStarted.toLowerCase().includes(lowerSearch);

  // ── Per-tab result counts for badges ──────────────────────────────────────
  const funcCount = lowerSearch
    ? (showOverview ? 1 : 0) + filteredFeatures.length + filteredSections.length + filteredUseCases.length
    : 0;

  const guideCount = lowerSearch ? (showGettingStarted ? 1 : 0) + filteredSteps.length + filteredProTips.length : 0;

  const faqCount = lowerSearch ? filteredFaq.length : 0;

  const funcHasResults = funcCount > 0;
  const guideHasResults = guideCount > 0;

  // ── Auto-switch to the tab with the most results when searching ───────────
  const prevSearch = React.useRef('');
  React.useEffect(() => {
    if (!lowerSearch) {
      // Only reset tab to functionality when search is fully cleared
      if (prevSearch.current) setActiveTab('functionality');
      prevSearch.current = '';
      return;
    }
    prevSearch.current = lowerSearch;
    const best = [
      { tab: 'functionality', count: funcCount },
      { tab: 'guide', count: guideCount },
      { tab: 'faq', count: faqCount },
    ].reduce((a, b) => (b.count > a.count ? b : a));
    if (best.count > 0) setActiveTab(best.tab);
  }, [lowerSearch, funcCount, guideCount, faqCount]);

  // ── Accordion open state ──────────────────────────────────────────────────
  // When searching: all matched items are open (derived from filtered arrays).
  // When not searching: user controls open state via openSteps / openFaqs.

  const activeStepValues = lowerSearch ? filteredSteps.map(({ originalIndex }) => `step-${originalIndex}`) : openSteps;

  const activeFaqValues = lowerSearch ? filteredFaq.map(({ originalIndex }) => `faq-${originalIndex}`) : openFaqs;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideCloseButton
        className="flex flex-col gap-0 p-0 w-full sm:!max-w-[600px] overflow-hidden"
      >
        {/* ── Fixed Header ─────────────────────────────────────────────────── */}
        <div className="bg-muted/50 border-b flex-shrink-0 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/20">
                <HelpCircle className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-foreground text-lg font-semibold leading-tight truncate">{content.pageTitle}</h2>
                <p className="text-muted-foreground text-sm mt-0.5 leading-snug">{content.description}</p>
              </div>
            </div>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 -mr-1 -mt-1 h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Close help panel"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close help panel</span>
              </Button>
            </SheetClose>
          </div>

          {/* Search bar */}
          <div className="relative mt-3">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none" />
            <Input
              placeholder="Search help content..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9 h-9 bg-background"
              aria-label="Search help content"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="text-muted-foreground hover:text-foreground absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Search summary — total results across all tabs */}
          {lowerSearch && (
            <p className="text-muted-foreground text-xs mt-2 leading-none">
              {funcCount + guideCount + faqCount === 0
                ? 'No results found'
                : `${funcCount + guideCount + faqCount} result${funcCount + guideCount + faqCount === 1 ? '' : 's'} across all tabs`}
            </p>
          )}
        </div>

        {/* ── Scrollable content ────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
            {/* Sticky tab bar */}
            <div className="sticky top-0 z-10 bg-background border-b px-5 pt-3 pb-0 flex-shrink-0">
              <TabsList className="grid w-full grid-cols-3 h-9">
                {/* Functionality tab */}
                <TabsTrigger value="functionality" className="gap-1.5 text-xs sm:text-sm relative">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  <span>Functionality</span>
                  {lowerSearch && funcCount > 0 && (
                    <span className="ml-1 bg-primary text-primary-foreground rounded-full text-[10px] font-bold leading-none px-1.5 py-0.5 min-w-[18px] text-center">
                      {funcCount}
                    </span>
                  )}
                </TabsTrigger>
                {/* User Guide tab */}
                <TabsTrigger value="guide" className="gap-1.5 text-xs sm:text-sm relative">
                  <BookOpen className="h-3.5 w-3.5 shrink-0" />
                  <span>User Guide</span>
                  {lowerSearch && guideCount > 0 && (
                    <span className="ml-1 bg-primary text-primary-foreground rounded-full text-[10px] font-bold leading-none px-1.5 py-0.5 min-w-[18px] text-center">
                      {guideCount}
                    </span>
                  )}
                </TabsTrigger>
                {/* FAQ tab */}
                <TabsTrigger value="faq" className="gap-1.5 text-xs sm:text-sm relative">
                  <HelpCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>FAQ</span>
                  {lowerSearch && faqCount > 0 && (
                    <span className="ml-1 bg-primary text-primary-foreground rounded-full text-[10px] font-bold leading-none px-1.5 py-0.5 min-w-[18px] text-center">
                      {faqCount}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ── Functionality Tab ──────────────────────────────────────────── */}
            <TabsContent value="functionality" className="mt-0 flex-1 overflow-y-auto">
              <div className="px-5 py-6 space-y-6">
                {/* Overview */}
                {showOverview && (
                  <section>
                    <SectionHeading>Overview</SectionHeading>
                    <p className="text-muted-foreground text-sm leading-relaxed mt-2">
                      <Highlight text={content.functionality.overview} query={lowerSearch} />
                    </p>
                  </section>
                )}

                {showOverview && filteredFeatures.length > 0 && <Separator />}

                {/* Key Features */}
                {filteredFeatures.length > 0 && (
                  <section>
                    <SectionHeading>Key Features</SectionHeading>
                    <ul className="space-y-3 mt-3">
                      {filteredFeatures.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                          <span className="text-sm leading-relaxed">
                            <span className="text-foreground font-medium">
                              <Highlight text={feature.label} query={lowerSearch} />
                            </span>
                            <span className="text-muted-foreground">
                              {' '}
                              — <Highlight text={feature.detail} query={lowerSearch} />
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {filteredFeatures.length > 0 && filteredSections.length > 0 && <Separator />}

                {/* Sections & Fields */}
                {filteredSections.length > 0 && (
                  <section>
                    <SectionHeading>Sections &amp; Fields</SectionHeading>
                    <div className="mt-3 border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="text-left text-muted-foreground font-medium px-3 py-2 w-[38%]">
                              Section / Field
                            </th>
                            <th className="text-left text-muted-foreground font-medium px-3 py-2">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {filteredSections.map((section, index) => (
                            <tr key={index} className={cn(index % 2 === 1 && 'bg-muted/20')}>
                              <td className="text-foreground font-medium px-3 py-2.5 align-top leading-snug">
                                <Highlight text={section.name} query={lowerSearch} />
                              </td>
                              <td className="text-muted-foreground px-3 py-2.5 leading-relaxed text-xs">
                                <Highlight text={section.description} query={lowerSearch} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {filteredSections.length > 0 && filteredUseCases.length > 0 && <Separator />}

                {/* Use Cases */}
                {filteredUseCases.length > 0 && (
                  <section>
                    <SectionHeading>Use Cases</SectionHeading>
                    <ul className="space-y-2 mt-3">
                      {filteredUseCases.map((useCase, index) => (
                        <li
                          key={index}
                          className="text-muted-foreground flex items-start gap-2 text-sm leading-relaxed"
                        >
                          <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                          <Highlight text={useCase} query={lowerSearch} />
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Empty state */}
                {!funcHasResults && lowerSearch && <EmptyState search={search} onClear={() => setSearch('')} />}
              </div>
            </TabsContent>

            {/* ── User Guide Tab ─────────────────────────────────────────────── */}
            <TabsContent value="guide" className="mt-0 flex-1 overflow-y-auto">
              <div className="px-5 py-6 space-y-6">
                {/* Getting Started callout */}
                {showGettingStarted && (
                  <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900 p-4">
                    <div className="flex items-start gap-3">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                      <div>
                        <p className="text-blue-900 dark:text-blue-100 text-sm font-semibold mb-1">Getting Started</p>
                        <p className="text-blue-800 dark:text-blue-200 text-sm leading-relaxed">
                          <Highlight text={content.userGuide.gettingStarted} query={lowerSearch} />
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step-by-step accordion */}
                {filteredSteps.length > 0 && (
                  <section>
                    <SectionHeading>
                      Step-by-Step Guide
                      {lowerSearch && (
                        <span className="ml-2 text-primary normal-case font-normal text-[11px]">
                          {filteredSteps.length} matching step{filteredSteps.length !== 1 ? 's' : ''} — all expanded
                        </span>
                      )}
                    </SectionHeading>
                    <Accordion
                      type="multiple"
                      value={activeStepValues}
                      onValueChange={lowerSearch ? undefined : setOpenSteps}
                      className="mt-3 space-y-2"
                    >
                      {filteredSteps.map(({ step, originalIndex }) => (
                        <AccordionItem
                          key={`step-${originalIndex}`}
                          value={`step-${originalIndex}`}
                          className="border rounded-lg overflow-hidden"
                        >
                          <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline hover:bg-muted/30 transition-colors">
                            <div className="flex items-center gap-3 text-left">
                              <Badge
                                variant="outline"
                                className="bg-primary/10 text-primary border-primary/20 shrink-0 h-5 min-w-[20px] px-1.5 text-xs font-semibold"
                              >
                                {originalIndex + 1}
                              </Badge>
                              <span>
                                <Highlight text={step.title} query={lowerSearch} />
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4 pt-0 border-t bg-muted/10">
                            <ol className="space-y-3 mt-3">
                              {step.items
                                .filter((item) => item.text || item.note || item.tip)
                                .map((item, itemIndex) => (
                                  <StepItem key={itemIndex} item={item} index={itemIndex} highlight={lowerSearch} />
                                ))}
                            </ol>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>
                )}

                {filteredSteps.length === 0 && lowerSearch && guideCount === 0 && (
                  <EmptyState search={search} onClear={() => setSearch('')} />
                )}

                {/* Pro Tips */}
                {filteredProTips.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-900 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                      <p className="text-amber-900 dark:text-amber-100 text-sm font-semibold">Pro Tips</p>
                    </div>
                    <ul className="space-y-2.5">
                      {filteredProTips.map((tip, index) => (
                        <li
                          key={index}
                          className="text-amber-800 dark:text-amber-200 flex items-start gap-2 text-sm leading-relaxed"
                        >
                          <span className="text-amber-500 mt-1 shrink-0 text-xs">•</span>
                          <Highlight text={tip} query={lowerSearch} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!guideHasResults && lowerSearch && <EmptyState search={search} onClear={() => setSearch('')} />}
              </div>
            </TabsContent>

            {/* ── FAQ Tab ────────────────────────────────────────────────────── */}
            <TabsContent value="faq" className="mt-0 flex-1 overflow-y-auto">
              <div className="px-5 py-6">
                {filteredFaq.length > 0 ? (
                  <>
                    {lowerSearch && (
                      <p className="text-muted-foreground text-xs mb-3">
                        {filteredFaq.length} matching question{filteredFaq.length !== 1 ? 's' : ''} — all expanded
                      </p>
                    )}
                    <Accordion
                      type="multiple"
                      value={activeFaqValues}
                      onValueChange={lowerSearch ? undefined : setOpenFaqs}
                      className="space-y-2"
                    >
                      {filteredFaq.map(({ item, originalIndex }) => (
                        <AccordionItem
                          key={`faq-${originalIndex}`}
                          value={`faq-${originalIndex}`}
                          className="border rounded-lg overflow-hidden"
                        >
                          <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline hover:bg-muted/30 transition-colors">
                            <div className="flex items-start gap-3 text-left">
                              <HelpCircle className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                              <span>
                                <Highlight text={item.question} query={lowerSearch} />
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4 pt-0 border-t bg-muted/10">
                            <div className="pt-3 pl-7">
                              <p className="text-muted-foreground text-sm leading-relaxed">
                                <Highlight text={item.answer} query={lowerSearch} />
                              </p>
                              {item.steps && item.steps.length > 0 && (
                                <ol className="mt-3 space-y-1.5 list-none">
                                  {item.steps.map((step, si) => (
                                    <li key={si} className="text-muted-foreground flex gap-2 text-sm">
                                      <span className="text-primary font-semibold shrink-0">{si + 1}.</span>
                                      <Highlight text={step} query={lowerSearch} />
                                    </li>
                                  ))}
                                </ol>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </>
                ) : (
                  <EmptyState search={search} onClear={() => setSearch('')} />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* ── Fixed Footer ─────────────────────────────────────────────────── */}
        <div className="bg-muted/50 border-t flex-shrink-0 px-5 py-3">
          <p className="text-muted-foreground text-xs leading-relaxed">
            Need more help? Contact your system administrator or raise a support ticket.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-1">
      {children}
    </h3>
  );
}

interface EmptyStateProps {
  search: string;
  onClear: () => void;
}

function EmptyState({ search, onClear }: EmptyStateProps) {
  return (
    <div className="text-center py-12">
      <div className="bg-muted/50 rounded-full h-12 w-12 flex items-center justify-center mx-auto mb-3">
        <Search className="text-muted-foreground h-5 w-5" />
      </div>
      <p className="text-foreground text-sm font-medium">No results found</p>
      <p className="text-muted-foreground text-xs mt-1">Nothing matched &ldquo;{search}&rdquo;</p>
      <button onClick={onClear} className="text-primary mt-2 text-xs hover:underline font-medium">
        Clear search
      </button>
    </div>
  );
}

// ─── Step Item ────────────────────────────────────────────────────────────────

interface StepItemProps {
  item: HelpStepItem;
  index: number;
  highlight: string;
}

function StepItem({ item, index, highlight }: StepItemProps) {
  return (
    <li className="space-y-2">
      {item.text && (
        <div className="flex items-start gap-2.5">
          <span className="text-primary mt-0.5 text-xs font-bold shrink-0 w-4 text-right leading-5">{index + 1}.</span>
          <span className="text-foreground text-sm leading-relaxed">
            <Highlight text={item.text} query={highlight} />
          </span>
        </div>
      )}

      {item.note && (
        <div
          className={cn(
            'rounded-md border px-3 py-2',
            item.text ? 'ml-6' : 'ml-0',
            'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700',
          )}
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500 dark:text-slate-400" />
            <p className="text-slate-700 dark:text-slate-300 text-xs leading-relaxed">
              <span className="font-semibold">Note: </span>
              <Highlight text={item.note} query={highlight} />
            </p>
          </div>
        </div>
      )}

      {item.tip && (
        <div
          className={cn(
            'rounded-md border px-3 py-2',
            item.text ? 'ml-6' : 'ml-0',
            'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
          )}
        >
          <div className="flex items-start gap-2">
            <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <p className="text-amber-800 dark:text-amber-200 text-xs leading-relaxed">
              <span className="font-semibold">Tip: </span>
              <Highlight text={item.tip} query={highlight} />
            </p>
          </div>
        </div>
      )}

      {item.subSteps && item.subSteps.length > 0 && (
        <ul className={cn('space-y-1.5 mt-0.5', item.text ? 'ml-6' : 'ml-0')}>
          {item.subSteps.map((sub, si) => (
            <li key={si} className="text-muted-foreground flex items-start gap-2 text-sm leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary/50 shrink-0" />
              <Highlight text={sub} query={highlight} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
